/**
 * MultiBenchmarkChart — Stock vs 6 benchmarks (SPY DIA QQQ IWM Sector Subsector)
 *
 * Renders one line per series, all rebased to the stock's starting price so
 * everything shares a single dollar Y-axis (visual divergence = relative
 * performance).  Includes the stock's anchored VWAP and a linear regression
 * line for direction.  Each line can be toggled on/off via pill buttons.
 *
 * Linked to the parent's chart timeframe (1d / 5d / 1mo / 3mo / 6mo / 1y) and
 * re-fetches automatically when the user changes timeframe.
 *
 * Performance box shows % gain/loss of each series for the selected window
 * plus the differential (stock − benchmark) for all six benchmarks.
 *
 * Props:
 *   ticker   – string
 *   timeframe – string (1d|5d|1mo|3mo|6mo|1y)
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import axios from "axios";
import {
  ComposedChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceArea, ReferenceLine,
} from "recharts";
import { BarChart3, RefreshCw, Eye, EyeOff, CandlestickChart, LineChart as LineChartIcon } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { API } from "../config/api";
import { enrichWithAnchoredVwap } from "../utils/vwap";
import YahooStyleCandlestick from "./YahooStyleCandlestick";

// Fixed broad-market benchmarks — always loaded
const INDEX_BENCHMARKS = [
  { key: "spy", label: "S&P 500",    symbol: "SPY", color: "#ffffff" },
  { key: "dia", label: "Dow Jones",  symbol: "DIA", color: "#fbbf24" }, // amber
  { key: "qqq", label: "Nasdaq 100", symbol: "QQQ", color: "#22d3ee" }, // cyan
  { key: "iwm", label: "Russell 2k", symbol: "IWM", color: "#f472b6" }, // pink
];

// Right-axis indicators (0-100 scale) — RSI/MFI come free from the existing
// /stock/indicators-chart endpoint; VIX has its own /stock/vix-chart endpoint;
// Volume is normalized per-bar (= bar_volume / max_volume × 100) so it fits
// the shared 0-100 axis while the tooltip still shows the raw share count.
// ATR(14) is Wilder's true range smoothed; values are in dollars but we
// normalize per-window to the 0-100 axis and show raw $X.XX in the tooltip.
const RIGHT_AXIS_INDICATORS = [
  { key: "rsi",    label: "RSI",     color: "#ef4444", strokeDasharray: undefined }, // red-500
  { key: "mfi",    label: "MFI",     color: "#22c55e", strokeDasharray: undefined }, // green-500
  { key: "vix",    label: "VIX",     color: "#fb923c", strokeDasharray: "3 3"     }, // orange-400 (dashed = different source)
  { key: "volume", label: "Volume",  color: "#a78bfa", strokeDasharray: undefined }, // violet-400 (normalized 0-100)
  { key: "atr",    label: "ATR(14)", color: "#0ea5e9", strokeDasharray: "5 3"     }, // sky-500 (normalized 0-100, raw $ in tooltip)
];
// Moving averages — computed on the stock's price series.  EMA = solid line,
// SMA = dashed line; both default OFF so the chart isn't crowded out of
// the box.  Distinct hues avoid collision with the benchmark palette above.
const MA_CONFIG = [
  // EMAs (solid)
  { key: "ema5",   label: "5 EMA",   period: 5,   kind: "ema", color: "#fb7185" }, // rose-400
  { key: "ema10",  label: "10 EMA",  period: 10,  kind: "ema", color: "#fdba74" }, // orange-300
  { key: "ema20",  label: "20 EMA",  period: 20,  kind: "ema", color: "#fde047" }, // yellow-300
  { key: "ema50",  label: "50 EMA",  period: 50,  kind: "ema", color: "#86efac" }, // green-300
  { key: "ema200", label: "200 EMA", period: 200, kind: "ema", color: "#5eead4" }, // teal-300
  // SMAs (dashed)
  { key: "sma5",   label: "5 SMA",   period: 5,   kind: "sma", color: "#93c5fd" }, // blue-300
  { key: "sma10",  label: "10 SMA",  period: 10,  kind: "sma", color: "#a5b4fc" }, // indigo-300
  { key: "sma20",  label: "20 SMA",  period: 20,  kind: "sma", color: "#c4b5fd" }, // violet-300
  { key: "sma50",  label: "50 SMA",  period: 50,  kind: "sma", color: "#f0abfc" }, // fuchsia-300
  { key: "sma200", label: "200 SMA", period: 200, kind: "sma", color: "#fda4af" }, // rose-300
];

const fmtPct = (v) => v == null ? "—" : `${v >= 0 ? "+" : ""}${Number(v).toFixed(2)}%`;
const pctClass = (v) => v == null ? "text-slate-300" : v >= 0 ? "text-emerald-300" : "text-rose-300";

async function fetchSeries(symbol, timeframe, intervalOverride, attempts = 3) {
  const params = { timeframe };
  if (intervalOverride && intervalOverride !== "auto") params.interval = intervalOverride;
  for (let i = 0; i < attempts; i++) {
    try {
      const r = await axios.get(`${API}/stock/indicators-chart/${symbol}`, {
        params, timeout: 35000,
      });
      const rows = r.data?.chart_data || [];
      if (rows.length) return { rows, prevClose: r.data?.prev_close ?? null };
    } catch (_e) { /* transient — retry below */ }
    if (i < attempts - 1) await new Promise(res => setTimeout(res, 1200 * (i + 1)));
  }
  return { rows: [], prevClose: null };
}

// Custom SVG shape for a single candle, rendered by Recharts inside a
// hidden `<Bar>` (see the ComposedChart below).  Recharts computes:
//   • `x`, `width` — the horizontal band for THIS category (via the
//     xAxis scale, already accounts for the number of visible bars),
//   • `y`, `height` — the pixel rect of the Bar's dataKey value against
//     the price-axis scale (we route the Bar through the "price" axis
//     so its y-scale matches every other price series on the chart).
// From those we can derive the pixel scale (px per $) and reproject
// open / high / low / close into pixel space for the wick + body.
//
// Design (updated 2026-03-02 for readability parity with TradingView):
//   • Green (#22c55e) up bar (close >= open), red (#ef4444) down bar.
//   • Body width = clamp(4, band*0.85, 22) → chunky bodies that fill
//     most of the x-band, matching classic OHLC-chart look.
//   • Body height clamped to a minimum of 4px so bodies remain visible
//     even when open ≈ close (e.g. 5-minute intraday bars with cents-
//     level moves against a $9 y-axis range would otherwise render as
//     invisible 0-1px lines).
//   • Wick stroke bumped to 1.5px so upper/lower shadows are visible
//     against the dark chart background.
//   • Any bar missing OHLC is skipped silently (returns null).
function CandleShape(props) {
  const { x, y, width, height, payload } = props;
  const o = payload?.candle_open;
  const h = payload?.candle_high;
  const l = payload?.candle_low;
  const c = payload?.candle_close;
  if (o == null || h == null || l == null || c == null) return null;
  // Reconstruct the linear scale used by the price y-axis (baseline is
  // 0 by default, so `k = height / high` gives pixels-per-dollar).
  const k = h > 0 ? height / h : 0;
  const py = (v) => y + (h - v) * k;
  const yHigh  = py(h);
  const yLow   = py(l);
  const yOpen  = py(o);
  const yClose = py(c);
  const up = c >= o;
  const color = up ? "#22c55e" : "#ef4444";

  // ── Body — proportional to the OPEN → CLOSE move, only a hair of
  //          minimum height so a true doji is a visible horizontal line
  //          but every "normal" bar keeps its natural relative size.
  //          This is what makes each candle distinctly shaped rather
  //          than all looking identical.
  const bodyW = Math.max(4, Math.min(24, (width || 8) * 0.85));
  const bodyX = (x + width / 2) - (bodyW / 2);
  const rawBodyH  = Math.abs(yClose - yOpen);
  const bodyH = Math.max(3, rawBodyH);          // 3px min → chunky but keeps natural variation
  const bodyTop = rawBodyH < 3
    ? (yOpen + yClose) / 2 - 1.5
    : Math.min(yOpen, yClose);

  // ── Wicks — always the TRUE high/low.  No artificial minimum tail,
  //   so bars with big shadows look tall and bars with small ranges
  //   look small.  This is what gives the chart the varied "candle
  //   forest" shape of a real TradingView / Yahoo candle chart.
  return (
    <g>
      <line
        x1={x + width / 2} x2={x + width / 2}
        y1={yHigh} y2={yLow}
        stroke={color} strokeWidth={2}
        strokeLinecap="round"
      />
      <rect
        x={bodyX} y={bodyTop}
        width={bodyW} height={bodyH}
        fill={color} stroke={color} strokeWidth={0.5}
      />
    </g>
  );
}

// Recognise the classic candlestick pattern formed by the most-recent
// 1-3 bars in an OHLC series.  Returns a rich descriptor:
//   { name, bias: "bullish"|"bearish"|"neutral", strength: "strong"|"moderate"|"weak", description }
// Patterns implemented (roughly following the Timon & Mati / Bulkowski
// candlestick cheat-sheet):
//   • Doji · Long-Legged Doji · Gravestone Doji · Dragonfly Doji
//   • Hammer · Inverted Hammer · Hanging Man · Shooting Star
//   • Bullish/Bearish Marubozu · Spinning Top
//   • Bullish/Bearish Engulfing · Piercing Line · Dark Cloud Cover
//   • Morning Star · Evening Star
//   • Three White Soldiers · Three Black Crows
// Returns null when no pattern is confidently detected — the info-box
// then shows a neutral "no distinct pattern" message rather than
// forcing a false signal.
export function detectCandlePattern(bars) {
  if (!bars || !bars.length) return null;
  // Work only on bars that have complete OHLC.
  const clean = bars.filter(b => b.candle_open != null && b.candle_high != null && b.candle_low != null && b.candle_close != null);
  if (clean.length < 1) return null;
  const c1 = clean[clean.length - 1];                // latest
  const c2 = clean.length >= 2 ? clean[clean.length - 2] : null;
  const c3 = clean.length >= 3 ? clean[clean.length - 3] : null;

  const feat = (b) => {
    const o = b.candle_open, h = b.candle_high, l = b.candle_low, c = b.candle_close;
    const range = Math.max(1e-9, h - l);
    const body = Math.abs(c - o);
    const upperShadow = h - Math.max(o, c);
    const lowerShadow = Math.min(o, c) - l;
    return {
      o, h, l, c, range, body,
      up: c >= o,
      bodyPct: body / range,
      upperPct: upperShadow / range,
      lowerPct: lowerShadow / range,
    };
  };
  const f1 = feat(c1);
  const f2 = c2 ? feat(c2) : null;
  const f3 = c3 ? feat(c3) : null;

  // ── 3-bar patterns ────────────────────────────────────────────────
  if (f2 && f3) {
    // Morning Star (bullish) — big red, small body, big green closing above midpoint of first red
    if (!f3.up && f3.bodyPct > 0.6 &&
        f2.bodyPct < 0.35 &&
        f1.up && f1.bodyPct > 0.5 &&
        f1.c > (f3.o + f3.c) / 2) {
      return {
        name: "Morning Star",
        bias: "bullish", strength: "strong",
        description: "Three-candle bullish reversal at the end of a downtrend: a large bearish candle, a small-bodied indecision candle, then a strong bullish candle that closes above the midpoint of the first. Signals momentum has shifted from sellers to buyers.",
      };
    }
    // Evening Star (bearish) — mirror
    if (f3.up && f3.bodyPct > 0.6 &&
        f2.bodyPct < 0.35 &&
        !f1.up && f1.bodyPct > 0.5 &&
        f1.c < (f3.o + f3.c) / 2) {
      return {
        name: "Evening Star",
        bias: "bearish", strength: "strong",
        description: "Three-candle bearish reversal at the top of an uptrend: a large bullish candle, a small-bodied indecision candle, then a strong bearish candle closing below the first candle's midpoint. Signals a shift from buyers to sellers.",
      };
    }
    // Three White Soldiers
    if (f3.up && f2.up && f1.up &&
        f2.c > f3.c && f1.c > f2.c &&
        f3.bodyPct > 0.55 && f2.bodyPct > 0.55 && f1.bodyPct > 0.55) {
      return {
        name: "Three White Soldiers",
        bias: "bullish", strength: "strong",
        description: "Three consecutive strong bullish candles, each closing higher than the previous with small upper shadows. Classic continuation/reversal pattern signalling sustained buying pressure.",
      };
    }
    // Three Black Crows
    if (!f3.up && !f2.up && !f1.up &&
        f2.c < f3.c && f1.c < f2.c &&
        f3.bodyPct > 0.55 && f2.bodyPct > 0.55 && f1.bodyPct > 0.55) {
      return {
        name: "Three Black Crows",
        bias: "bearish", strength: "strong",
        description: "Three consecutive strong bearish candles, each closing lower than the previous with small lower shadows. Signals sustained selling pressure and often marks the start of a downtrend.",
      };
    }
  }

  // ── 2-bar patterns ────────────────────────────────────────────────
  if (f2) {
    // Bullish Engulfing — prev bearish, current bullish body engulfs prev body
    if (!f2.up && f1.up &&
        f1.o <= f2.c && f1.c >= f2.o &&
        f1.body > f2.body * 1.05) {
      return {
        name: "Bullish Engulfing",
        bias: "bullish", strength: "strong",
        description: "A large bullish candle whose body completely engulfs the prior bearish candle's body. Signals that buyers have overwhelmed sellers — a common reversal pattern at the end of a downtrend.",
      };
    }
    // Bearish Engulfing
    if (f2.up && !f1.up &&
        f1.o >= f2.c && f1.c <= f2.o &&
        f1.body > f2.body * 1.05) {
      return {
        name: "Bearish Engulfing",
        bias: "bearish", strength: "strong",
        description: "A large bearish candle whose body completely engulfs the prior bullish candle's body. Signals that sellers have overwhelmed buyers — a common reversal pattern at the end of an uptrend.",
      };
    }
    // Piercing Line
    if (!f2.up && f1.up &&
        f1.o < f2.l &&
        f1.c > (f2.o + f2.c) / 2 && f1.c < f2.o) {
      return {
        name: "Piercing Line",
        bias: "bullish", strength: "moderate",
        description: "A bullish candle opens below the previous bearish candle's low and closes above its midpoint. Signals a potential bottom and reversal — weaker than an Engulfing but still meaningful.",
      };
    }
    // Dark Cloud Cover
    if (f2.up && !f1.up &&
        f1.o > f2.h &&
        f1.c < (f2.o + f2.c) / 2 && f1.c > f2.o) {
      return {
        name: "Dark Cloud Cover",
        bias: "bearish", strength: "moderate",
        description: "A bearish candle opens above the previous bullish candle's high and closes below its midpoint. Signals rejection at higher prices and a potential top — weaker than a Bearish Engulfing.",
      };
    }
  }

  // ── Single-bar patterns ──────────────────────────────────────────
  // Doji variants (very small body relative to range)
  if (f1.bodyPct < 0.08) {
    if (f1.upperPct > 0.6 && f1.lowerPct < 0.1) {
      return {
        name: "Gravestone Doji",
        bias: "bearish", strength: "moderate",
        description: "A doji where the open, low and close are all near the session low, with a long upper shadow. Buyers pushed higher but sellers slammed the price back down — often a top-of-range warning.",
      };
    }
    if (f1.lowerPct > 0.6 && f1.upperPct < 0.1) {
      return {
        name: "Dragonfly Doji",
        bias: "bullish", strength: "moderate",
        description: "A doji where open, high and close cluster near the session high with a long lower shadow. Sellers drove price down but buyers reclaimed the level by the close — often a bottom-of-range signal.",
      };
    }
    if (f1.upperPct > 0.35 && f1.lowerPct > 0.35) {
      return {
        name: "Long-Legged Doji",
        bias: "neutral", strength: "weak",
        description: "Long upper AND lower shadows with a near-zero body. Signals extreme indecision — neither buyers nor sellers won the session. Often marks a pivot point when it appears after a strong trend.",
      };
    }
    return {
      name: "Doji",
      bias: "neutral", strength: "weak",
      description: "Open and close are virtually equal, forming a cross or tiny body. Signals indecision — a pause in the trend that often precedes a reversal when confirmed by the next candle.",
    };
  }
  // Hammer / Hanging Man — long lower shadow, small body near top
  if (f1.lowerPct > 0.55 && f1.upperPct < 0.15 && f1.bodyPct < 0.35) {
    // We can't easily tell uptrend vs downtrend from a single bar, so
    // return the bullish reversal interpretation (Hammer) — users cross-
    // reference the chart trend visually.  The description covers both.
    return {
      name: "Hammer",
      bias: "bullish", strength: "moderate",
      description: "A small body near the top with a long lower shadow at least twice the body. When it appears at the end of a downtrend it signals a bullish reversal (Hammer); at the top of an uptrend the same shape is a bearish 'Hanging Man'. Check the surrounding trend to confirm.",
    };
  }
  // Inverted Hammer / Shooting Star — long upper shadow, small body near bottom
  if (f1.upperPct > 0.55 && f1.lowerPct < 0.15 && f1.bodyPct < 0.35) {
    return {
      name: "Inverted Hammer",
      bias: "bullish", strength: "moderate",
      description: "A small body near the bottom with a long upper shadow at least twice the body. At the end of a downtrend it's a bullish reversal (Inverted Hammer); at the top of an uptrend it's the bearish 'Shooting Star'. Trend context resolves the ambiguity.",
    };
  }
  // Marubozu — full body, no shadows
  if (f1.bodyPct > 0.9 && f1.upperPct < 0.05 && f1.lowerPct < 0.05) {
    if (f1.up) return {
      name: "Bullish Marubozu",
      bias: "bullish", strength: "strong",
      description: "A tall bullish candle with no shadows — buyers controlled the entire session from open to close. Strong continuation signal, especially when volume is elevated.",
    };
    return {
      name: "Bearish Marubozu",
      bias: "bearish", strength: "strong",
      description: "A tall bearish candle with no shadows — sellers controlled the entire session from open to close. Strong continuation signal to the downside.",
    };
  }
  // Spinning Top
  if (f1.bodyPct < 0.35 && f1.upperPct > 0.25 && f1.lowerPct > 0.25) {
    return {
      name: "Spinning Top",
      bias: "neutral", strength: "weak",
      description: "Small body with meaningful shadows on both sides. Signals indecision and loss of momentum — often precedes a reversal, but needs confirmation from the next candle.",
    };
  }
  return null;
}

// ────────────────────────────────────────────────────────────────────

export default function MultiBenchmarkChart({ ticker, timeframe = "1d", navBookValue = null, canonValues = null }) {
  const [stockBars, setStockBars] = useState([]);
  const [vixBars, setVixBars] = useState([]);
  const [benchBars, setBenchBars] = useState({});  // { spy:[], dia:[], qqq:[], iwm:[], sector:[], subsector:[] }
  // Prev-window closes per series — canonical % baseline (Overview semantics)
  const [prevCloses, setPrevCloses] = useState({});
  const [etfMap, setEtfMap] = useState({ sector_etf: null, subsector_etf: null, sector_label: null, subsector_label: null });
  const [fairValueSeries, setFairValueSeries] = useState(null); // { type, series[], ... } for the current ticker+timeframe
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Visibility toggles — stock + regression + VWAP on by default;
  // SPY on by default; DIA/QQQ/IWM/Sector/Subsector also on by default but
  // user can hide them via the pill buttons.  All 10 EMAs/SMAs default OFF
  // so the chart isn't crowded; user clicks the toggles to bring them in.
  const [visible, setVisible] = useState({
    stock: true, regression: true, vwap: true,
    spy: true, dia: true, qqq: true, iwm: true,
    sector: true, subsector: true,
    // Fair value overlay (NAV time-series for ETFs, Book Value % for stocks)
    // — default ON so the divergence is immediately visible when relevant.
    fair_value: true,
    // Calculated iNAV — independent 3rd method (SEC data + basket).
    // Default OFF because the primary + anchor already give a good picture,
    // but user can turn on for redundancy / when primary breaks.
    calc_inav: false,
    // Anchor NAV — yesterday's struck NAV, rendered as a horizontal
    // reference line on the price axis so the user has context on where
    // the fund's last-published NAV sat vs. where the basket is now.
    anchor_nav: true,
    // Right-axis indicators — default OFF (chart is already busy)
    rsi: false, mfi: false, vix: false, volume: false, atr: false,
    // Support/resistance zones — default ON so they're discoverable
    zones: true,
    // 2× ATR stop reference line — default ON, hidden when no ATR available
    atr_stop: true,
    // 1× ATR trailing-alert reference line — default ON
    atr_alert: true,
    // Shaded warning band between 1× and 2× ATR — default ON
    atr_zone: true,
    ...Object.fromEntries(MA_CONFIG.map(m => [m.key, false])),
  });

  const toggle = (key) => setVisible(v => ({ ...v, [key]: !v[key] }));

  // ── Line ↔ Candle chart-type toggle ─────────────────────────────
  // In BOTH modes the toggles paint on the chart (Line mode via Recharts,
  // Candle mode via TradingView Lightweight Charts).  So we no longer
  // hide overlays on mode-swap — the user's picks stay live across the
  // toggle.  We still auto-tighten the candle interval on the 1D view
  // so intraday bars are readable (Yahoo uses 1-minute bars there).
  const [chartType, setChartType] = useState("line");
  // ── AI Consolidation detection (candle mode) ─────────────────────
  const [showConsolidation, setShowConsolidation] = useState(false);
  const [consolZones, setConsolZones] = useState([]);
  const [consolBusy, setConsolBusy] = useState(false);
  const setChartTypeSafely = useCallback((nextType) => {
    setChartType(prev => {
      if (prev === nextType) return prev;
      if (nextType === "candle" && prev === "line") {
        if (timeframe === "1d") setCandleInterval("1m");
      } else if (nextType === "line" && prev === "candle") {
        setCandleInterval("auto");
      }
      return nextType;
    });
  }, [timeframe]);
  // User-selectable candle bar interval.  "auto" defers to the
  // timeframe's default (see CHART_TIMEFRAME_MAP on the backend); any
  // other value is passed straight through to yfinance as an interval
  // override (1m / 2m / 5m / 15m / 30m / 60m / 90m / 1d / 5d / 1wk / 1mo).
  // Yahoo caps intraday history (1m→7d, others→60d); the backend
  // auto-clamps the period so requests never come back empty.
  const [candleInterval, setCandleInterval] = useState("auto");
  const loadRetryRef = useRef(0);
  // Horizontal zoom (100–500%).  Scales the plot area width via a
  // wrapper div, then the chart component fills that width via
  // ResponsiveContainer — bars spread out proportionally as zoom
  // increases, making thin bodies (esp. intraday) actually readable.
  // Excel-style: `−` / slider / `+`.
  const [zoom, setZoom] = useState(100);
  // Human-readable default candle bar size for the current timeframe.
  // Used only in the "auto" branch of the header label.
  const defaultCandleLabel = ({
    "1d": "5-minute", "5d": "1-hour",
    "10d": "daily",  "20d": "daily",   "50d": "daily",
    "100d": "daily", "200d": "daily",  "1y":  "daily",
    "1mo": "daily",  "3mo": "daily",   "6mo": "daily",
    "mtd": "daily",  "ytd": "daily",
    "3y":  "monthly","5y":  "monthly",
  })[timeframe] || "daily";
  // If the user picked a specific interval from the dropdown, use its
  // pretty label instead — otherwise defer to the timeframe default.
  const candleIntervalLabel = candleInterval === "auto"
    ? defaultCandleLabel
    : ({ "1m":"1-minute", "2m":"2-minute", "5m":"5-minute", "15m":"15-minute",
         "30m":"30-minute", "60m":"1-hour", "90m":"90-minute",
         "1d":"daily", "5d":"5-day", "1wk":"weekly", "1mo":"monthly" }[candleInterval]
       || candleInterval);

  // 1) Resolve sector + subsector ETFs whenever the ticker changes
  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    setEtfMap({ sector_etf: null, subsector_etf: null, sector_label: null, subsector_label: null });
    axios.get(`${API}/early-warnings/etfs-for/${ticker}`, { timeout: 10000 })
      .then(r => { if (!cancelled) setEtfMap(r.data || {}); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [ticker]);

  // 2) Load stock + all benchmark series in parallel whenever ticker / timeframe / etfMap / candleInterval changes.
  // We pass `candleInterval` to every fetch so all series (stock,
  // benchmarks, VIX) share the same X-axis grid — otherwise a
  // user-picked 5-minute stock bar wouldn't line up with the default
  // daily SPY bar.
  const load = useCallback(async () => {
    if (!ticker) return;
    setLoading(true); setError(null);
    try {
      const iv = candleInterval;  // "auto" | "1m" | "5m" | … | "1mo"
      const promises = {
        stock: fetchSeries(ticker, timeframe, iv),
        spy:   fetchSeries("SPY", timeframe, iv),
        dia:   fetchSeries("DIA", timeframe, iv),
        qqq:   fetchSeries("QQQ", timeframe, iv),
        iwm:   fetchSeries("IWM", timeframe, iv),
        sector:    etfMap.sector_etf    ? fetchSeries(etfMap.sector_etf, timeframe, iv)    : Promise.resolve({ rows: [], prevClose: null }),
        subsector: etfMap.subsector_etf ? fetchSeries(etfMap.subsector_etf, timeframe, iv) : Promise.resolve({ rows: [], prevClose: null }),
        // VIX comes from its own endpoint and is keyed by stock-bar time
        vix: axios
          .get(`${API}/stock/vix-chart/${ticker}`, { params: { timeframe }, timeout: 35000 })
          .then(r => r.data?.chart_data || [])
          .catch(() => []),
      };
      const entries = await Promise.all(Object.entries(promises).map(async ([k, p]) => [k, await p]));
      const got = Object.fromEntries(entries);
      setStockBars(got.stock.rows || []);
      setVixBars(got.vix || []);
      setBenchBars({
        spy: got.spy.rows, dia: got.dia.rows, qqq: got.qqq.rows, iwm: got.iwm.rows,
        sector: got.sector.rows, subsector: got.subsector.rows,
      });
      setPrevCloses({
        stock: got.stock.prevClose, spy: got.spy.prevClose, dia: got.dia.prevClose,
        qqq: got.qqq.prevClose, iwm: got.iwm.prevClose,
        sector: got.sector.prevClose, subsector: got.subsector.prevClose,
      });
      // First-try reliability: if the primary series still came back empty
      // after fetchSeries' internal retries, schedule a full reload.
      if (!got.stock.rows?.length && loadRetryRef.current < 2) {
        loadRetryRef.current += 1;
        setTimeout(() => { load(); }, 2500);
      } else if (got.stock.rows?.length) {
        loadRetryRef.current = 0;
      }
    } catch (e) {
      setError(e?.message || "Failed to load comparison data");
    }
    setLoading(false);
  }, [ticker, timeframe, etfMap.sector_etf, etfMap.subsector_etf, candleInterval]);

  useEffect(() => { load(); }, [load]);

  // Fetch the timeframe-specific fair-value series (ETF iNAV curve OR
  // stock book-value-as-% curve) whenever ticker/timeframe changes.
  useEffect(() => {
    if (!ticker || ticker === "MKTAVG") { setFairValueSeries(null); return; }
    let cancelled = false;
    setFairValueSeries(null);
    axios.get(`${API}/stock/fair-value-series/${encodeURIComponent(ticker)}`, {
      params: { timeframe }, timeout: 30000,
    })
      .then((r) => { if (!cancelled) setFairValueSeries(r.data || null); })
      .catch(() => { if (!cancelled) setFairValueSeries(null); });
    return () => { cancelled = true; };
  }, [ticker, timeframe]);

  // Canonical Overview values for MKTAVG — used to anchor the chart's %
  // baselines so displayed gain/loss EXACTLY equals the Overview page.
  const CANON_TIMEFRAMES = ["1d","5d","10d","20d","50d","100d","200d","1y","3y","5y"];
  const [canonFetched, setCanonFetched] = useState(null);
  useEffect(() => {
    // Skip the self-fetch when the parent supplies canonValues (keeps cards
    // and chart on the exact same object / refresh cycle).
    if (canonValues || ticker !== "MKTAVG" || !CANON_TIMEFRAMES.includes(timeframe)) { setCanonFetched(null); return; }
    let cancelled = false;
    setCanonFetched(null);
    axios.get(`${API}/market/indicators-history`, {
      params: { timeframe, values_only: true }, timeout: 30000,
    })
      .then(r => { if (!cancelled) setCanonFetched(r.data?.current_values || null); })
      .catch(() => { if (!cancelled) setCanonFetched(null); });
    return () => { cancelled = true; };
  }, [ticker, timeframe, !!canonValues]);
  const canonVals = canonValues ?? canonFetched;

  // 3) Build the merged chart data: rebase every benchmark to the stock's
  //    first price, attach to the row keyed by time, plus compute regression
  //    + VWAP on the stock series.
  const { chartData, performance, regressionStats, support, resistance,
          atrPeriod, lastAtr, atrStopLevel, atrAlertLevel } = useMemo(() => {
    if (!stockBars || stockBars.length < 3) {
      return { chartData: [], performance: null, regressionStats: null, support: null, resistance: null };
    }
    const stockPrices = stockBars.map(d => d.price).filter(p => p > 0);
    if (stockPrices.length < 3) {
      return { chartData: [], performance: null, regressionStats: null, support: null, resistance: null };
    }
    const stockStart = stockPrices[0];
    const stockEnd   = stockPrices[stockPrices.length - 1];
    // MKTAVG (market composite) percentages are anchored so the displayed
    // gain/loss EXACTLY equals the Overview page: baseline = lastPrice /
    // (1 + canonicalPct/100), falling back to the prev-window close.
    const isMktComposite = ticker === "MKTAVG";
    const stockBase = (() => {
      if (!isMktComposite) return stockStart;
      if (canonVals?.market != null && stockEnd > 0) return stockEnd / (1 + canonVals.market / 100);
      if (prevCloses.stock > 0) return prevCloses.stock;
      return stockStart;
    })();
    const stockPct   = ((stockEnd / stockBase - 1) * 100);

    // Support / resistance from min/max of the visible window.
    const supp = Math.min(...stockPrices);
    const resi = Math.max(...stockPrices);

    // Rebase helper: build {time -> rebased_price} lookup from a benchmark series.
    // For MKTAVG each benchmark is anchored to its canonical Overview value
    // (fallback: its own prev-window close) so its displayed % equals the
    // Overview page value for that index.
    const rebaseLookup = (bars, benchPrev, benchCanon) => {
      if (!bars || bars.length === 0) return { map: {}, pct: null, start: null, end: null };
      const prices = bars.map(d => d.price).filter(p => p > 0);
      if (prices.length < 2) return { map: {}, pct: null, start: null, end: null };
      const start = prices[0];
      const end   = prices[prices.length - 1];
      let base = start;
      if (isMktComposite) {
        if (benchCanon != null && end > 0) base = end / (1 + benchCanon / 100);
        else if (benchPrev > 0) base = benchPrev;
      }
      const scale = stockBase / base;
      const map = {};
      bars.forEach(d => { if (d.price > 0) map[d.time] = Math.round(d.price * scale * 100) / 100; });
      return { map, pct: (end / base - 1) * 100, start, end };
    };

    const rebased = {
      spy:       rebaseLookup(benchBars.spy, prevCloses.spy, canonVals?.spy),
      dia:       rebaseLookup(benchBars.dia, prevCloses.dia, canonVals?.dia),
      qqq:       rebaseLookup(benchBars.qqq, prevCloses.qqq, canonVals?.qqq),
      iwm:       rebaseLookup(benchBars.iwm, prevCloses.iwm, canonVals?.iwm),
      sector:    rebaseLookup(benchBars.sector, prevCloses.sector, null),
      subsector: rebaseLookup(benchBars.subsector, prevCloses.subsector, null),
    };

    // VIX lookup by time (raw value, 0-100 axis)
    const vixByTime = {};
    (vixBars || []).forEach(d => { if (d?.vix != null) vixByTime[d.time] = d.vix; });

    // Fair-value series lookup by time.  For ETFs it's a NAV $-value keyed
    // onto the price axis (already anchored to yesterday's ETF close so
    // the last bar's iNAV matches the live badge exactly).  For stocks
    // it's a "book value as % of price" number in the 0-100 range keyed
    // onto the right-hand indicator axis.
    const fvByTime = {};
    const fvIsEtf = fairValueSeries?.type === "ETF";
    if (fairValueSeries?.series?.length) {
      fairValueSeries.series.forEach(d => { fvByTime[d.time] = d.value; });
    }
    // Calculated iNAV series (independent 3rd method) — keyed the same
    // way as the primary series.  Amber line on the price axis.
    const calcByTime = {};
    if (fairValueSeries?.calc_series?.length) {
      fairValueSeries.calc_series.forEach(d => { calcByTime[d.time] = d.value; });
    }

    // Volume normalization for the 0-100 right axis.  Tooltip still shows
    // the raw share count (formatted as 13.1M / 145K).
    const volumes = stockBars.map(d => (d?.volume != null ? d.volume : 0));
    const maxVol  = Math.max(...volumes, 0);
    const volScale = maxVol > 0 ? 100 / maxVol : 0;

    // Wilder's ATR: TR_i = max(high-low, |high-prev_close|, |low-prev_close|),
    // then smoothed. Period is canonical 14, but for short timeframes (e.g.
    // 10D = 10 bars) we drop to max(2, n-1) so the line still renders.
    // Tracks the actual period used so the toggle label can show it.
    const CANONICAL_ATR_PERIOD = 14;
    const ATR_PERIOD = stockBars.length > CANONICAL_ATR_PERIOD
      ? CANONICAL_ATR_PERIOD
      : Math.max(2, stockBars.length - 1);
    const atrRaw = new Array(stockBars.length).fill(null);
    if (stockBars.length > ATR_PERIOD) {
      const trs = new Array(stockBars.length).fill(null);
      for (let i = 1; i < stockBars.length; i++) {
        const d = stockBars[i], prev = stockBars[i - 1];
        const h = d?.high ?? d?.price, lo = d?.low ?? d?.price;
        const pc = prev?.price;
        if (h == null || lo == null || pc == null) continue;
        trs[i] = Math.max(h - lo, Math.abs(h - pc), Math.abs(lo - pc));
      }
      // Seed initial ATR as simple avg of first `period` TRs
      let seed = 0, seeded = 0;
      for (let i = 1; i <= ATR_PERIOD && i < trs.length; i++) {
        if (trs[i] != null) { seed += trs[i]; seeded++; }
      }
      if (seeded >= Math.max(2, ATR_PERIOD - 1)) {
        atrRaw[ATR_PERIOD] = seed / seeded;
        for (let i = ATR_PERIOD + 1; i < trs.length; i++) {
          if (trs[i] == null || atrRaw[i - 1] == null) continue;
          atrRaw[i] = (atrRaw[i - 1] * (ATR_PERIOD - 1) + trs[i]) / ATR_PERIOD;
        }
      }
    }
    const atrValues = atrRaw.filter(v => v != null);
    const maxAtr   = atrValues.length ? Math.max(...atrValues) : 0;
    const atrScale = maxAtr > 0 ? 100 / maxAtr : 0;
    // Latest non-null ATR — used by the 1× / 2× ATR alert/stop reference lines.
    let lastAtrRaw = null;
    for (let i = atrRaw.length - 1; i >= 0; i--) {
      if (atrRaw[i] != null) { lastAtrRaw = atrRaw[i]; break; }
    }
    const atrStopLevel = lastAtrRaw != null && stockEnd != null
      ? Math.round((stockEnd - 2 * lastAtrRaw) * 100) / 100
      : null;
    const atrAlertLevel = lastAtrRaw != null && stockEnd != null
      ? Math.round((stockEnd - 1 * lastAtrRaw) * 100) / 100
      : null;

    // Linear regression on stock's price series
    const n = stockPrices.length;
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
    stockPrices.forEach((p, i) => { sumX += i; sumY += p; sumXY += i*p; sumX2 += i*i; });
    const denom = n * sumX2 - sumX * sumX;
    const slope = denom !== 0 ? (n * sumXY - sumX * sumY) / denom : 0;
    const intercept = (sumY - slope * sumX) / n;

    // Compute EMA & SMA series for every configured period.  Indexed by
    // position within the price-only array (i.e. excluding null bars).
    // Returns array of length stockPrices.length with leading nulls until
    // enough bars are available.
    const computeSma = (arr, period) => {
      if (arr.length < period) return new Array(arr.length).fill(null);
      const out = new Array(arr.length).fill(null);
      let sum = 0;
      for (let i = 0; i < arr.length; i++) {
        sum += arr[i];
        if (i >= period) sum -= arr[i - period];
        if (i >= period - 1) out[i] = sum / period;
      }
      return out;
    };
    const computeEma = (arr, period) => {
      if (arr.length < period) return new Array(arr.length).fill(null);
      const k = 2 / (period + 1);
      const out = new Array(arr.length).fill(null);
      // Seed EMA with the SMA of the first `period` bars
      let seed = 0;
      for (let i = 0; i < period; i++) seed += arr[i];
      seed /= period;
      out[period - 1] = seed;
      for (let i = period; i < arr.length; i++) {
        out[i] = arr[i] * k + out[i - 1] * (1 - k);
      }
      return out;
    };
    const maSeries = {};
    MA_CONFIG.forEach(m => {
      maSeries[m.key] = m.kind === "ema"
        ? computeEma(stockPrices, m.period)
        : computeSma(stockPrices, m.period);
    });

    // VWAP per bar (anchored to period start)
    const withVwap = enrichWithAnchoredVwap(stockBars);

    let priceIdx = 0;
    const rows = withVwap.map((d, idx) => {
      const reg = d.price > 0 ? Math.round((intercept + slope * priceIdx) * 100) / 100 : null;
      // MA values are indexed by position within the price-only array.
      const maVals = {};
      if (d.price > 0) {
        MA_CONFIG.forEach(m => {
          const v = maSeries[m.key][priceIdx];
          maVals[m.key] = v == null ? null : Math.round(v * 100) / 100;
        });
        priceIdx++;
      } else {
        MA_CONFIG.forEach(m => { maVals[m.key] = null; });
      }
      const atrR = atrRaw[idx];
      // Fair-value overlay: ETFs → NAV $ on price axis (raw value, no
      // rescaling — anchor is yesterday's ETF close so the current-bar
      // value matches the live iNAV badge to the penny).  Stocks →
      // book% on 0-100 right axis.
      const fvRaw = fvByTime[d.time];
      const fvPrice = fvIsEtf && fvRaw != null ? Math.round(fvRaw * 100) / 100 : null;
      const fvPct   = !fvIsEtf && fvRaw != null ? Math.round(fvRaw * 100) / 100 : null;
      // Calculated iNAV (3rd method) — independent SEC-based series
      const calcRaw = calcByTime[d.time];
      const calcPrice = fvIsEtf && calcRaw != null ? Math.round(calcRaw * 100) / 100 : null;
      return {
        time: d.time,
        stock: d.price > 0 ? d.price : null,
        // Raw OHLC bars — carried through so the candlestick renderer can
        // draw wicks (high/low) + bodies (open→close) using the exact same
        // bars the line chart uses.  Nullable when the bar had missing
        // fields (e.g. Yahoo returned Close only).
        candle_open:  d?.open  ?? null,
        candle_high:  d?.high  ?? null,
        candle_low:   d?.low   ?? null,
        candle_close: d?.price ?? null,
        vwap:  d.vwap ?? null,
        regression: reg,
        // Fair-value series — keyed per bar so it renders as a proper
        // moving line (never a horizontal reference).
        fair_value_price: fvPrice,
        fair_value_pct:   fvPct,
        calc_inav_price:  calcPrice,
        // Right-axis indicators (0-100): RSI/MFI come from stockBars,
        // VIX from its own keyed lookup, Volume is rescaled per-bar.
        rsi: d.rsi ?? null,
        mfi: d.mfi ?? null,
        vix: vixByTime[d.time] ?? null,
        volume: d?.volume != null ? Math.round(d.volume * volScale * 100) / 100 : null,
        volume_raw: d?.volume ?? null,
        atr: atrR != null ? Math.round(atrR * atrScale * 100) / 100 : null,
        atr_raw: atrR != null ? Math.round(atrR * 100) / 100 : null,
        spy:       rebased.spy.map[d.time]       ?? null,
        dia:       rebased.dia.map[d.time]       ?? null,
        qqq:       rebased.qqq.map[d.time]       ?? null,
        iwm:       rebased.iwm.map[d.time]       ?? null,
        sector:    rebased.sector.map[d.time]    ?? null,
        subsector: rebased.subsector.map[d.time] ?? null,
        ...maVals,
      };
    });

    const round = v => v == null ? null : Math.round(v * 100) / 100;
    const perf = {
      stockPct: round(stockPct),
      stockStart, stockEnd,
      spy:       { pct: round(rebased.spy.pct),       diff: rebased.spy.pct       != null ? round(stockPct - rebased.spy.pct)       : null, start: rebased.spy.start,       end: rebased.spy.end },
      dia:       { pct: round(rebased.dia.pct),       diff: rebased.dia.pct       != null ? round(stockPct - rebased.dia.pct)       : null, start: rebased.dia.start,       end: rebased.dia.end },
      qqq:       { pct: round(rebased.qqq.pct),       diff: rebased.qqq.pct       != null ? round(stockPct - rebased.qqq.pct)       : null, start: rebased.qqq.start,       end: rebased.qqq.end },
      iwm:       { pct: round(rebased.iwm.pct),       diff: rebased.iwm.pct       != null ? round(stockPct - rebased.iwm.pct)       : null, start: rebased.iwm.start,       end: rebased.iwm.end },
      sector:    { pct: round(rebased.sector.pct),    diff: rebased.sector.pct    != null ? round(stockPct - rebased.sector.pct)    : null, start: rebased.sector.start,    end: rebased.sector.end },
      subsector: { pct: round(rebased.subsector.pct), diff: rebased.subsector.pct != null ? round(stockPct - rebased.subsector.pct) : null, start: rebased.subsector.start, end: rebased.subsector.end },
    };

    return {
      chartData: rows,
      performance: perf,
      regressionStats: { slope: round(slope), startPrice: round(intercept), endPrice: round(intercept + slope * (n - 1)) },
      support: round(supp),
      resistance: round(resi),
      atrPeriod: ATR_PERIOD,
      lastAtr: lastAtrRaw != null ? round(lastAtrRaw) : null,
      atrStopLevel,
      atrAlertLevel,
    };
  }, [stockBars, benchBars, vixBars, fairValueSeries, prevCloses, canonVals, ticker]);

  // Fetch AI consolidation zones whenever the feature is ON in candle mode.
  useEffect(() => {
    if (!showConsolidation || chartType !== "candle" || !chartData?.length) {
      setConsolZones([]);
      return;
    }
    let cancelled = false;
    setConsolBusy(true);
    axios.post(`${API}/chart-ai/consolidation`, {
      bars: chartData.map(b => ({
        o: b.candle_open, h: b.candle_high, l: b.candle_low, c: b.candle_close, t: b.time,
      })),
    }, { timeout: 20000 })
      .then(r => { if (!cancelled) setConsolZones(r.data?.zones || []); })
      .catch(() => { if (!cancelled) setConsolZones([]); })
      .finally(() => { if (!cancelled) setConsolBusy(false); });
    return () => { cancelled = true; };
  }, [showConsolidation, chartType, chartData]);

  // ----- UI ------------------------------------------------------------------

  // Build the full set of toggleable lines (including dynamic sector/subsector + EMAs/SMAs)
  const lines = useMemo(() => {
    const list = [
      { key: "stock",      label: ticker,                       color: "#a855f7", strokeWidth: 2.5,                            kind: "core" },
      { key: "regression", label: "Regression",                 color: "#f97316", strokeWidth: 2.5, strokeDasharray: "5 5",    kind: "core" },
      { key: "vwap",       label: "VWAP",                       color: "#ee82ee", strokeWidth: 2.5, strokeDasharray: "4 3",    kind: "core" },
      ...INDEX_BENCHMARKS.map(b => ({ ...b, strokeWidth: 1.8, kind: "index" })),
    ];
    if (etfMap.sector_etf) {
      list.push({
        key: "sector",
        label: `${etfMap.sector_etf}${etfMap.sector_label ? ` · ${etfMap.sector_label}` : ""}`,
        color: "#34d399", strokeWidth: 2, kind: "etf",
      });
    }
    if (etfMap.subsector_etf) {
      list.push({
        key: "subsector",
        label: `${etfMap.subsector_etf}${etfMap.subsector_label ? ` · ${etfMap.subsector_label}` : ""}`,
        color: "#a3e635", strokeWidth: 2, kind: "etf",
      });
    }
    // EMAs (solid) + SMAs (dashed)
    MA_CONFIG.forEach(m => {
      list.push({
        key: m.key,
        label: m.label,
        color: m.color,
        strokeWidth: 2.2,
        strokeDasharray: m.kind === "sma" ? "4 2" : undefined,
        kind: "ma",
        period: m.period,
      });
    });
    return list;
  }, [ticker, etfMap]);

  // How many stock bars do we have?  Drives EMA/SMA "insufficient data" flag.
  const stockBarCount = useMemo(
    () => (stockBars || []).filter(d => d.price > 0).length,
    [stockBars],
  );

  // Custom tooltip — only show visible series, format as $ for price axis,
  // plain number for RSI/MFI/VIX, and raw shares (1.23M/145K) for Volume.
  // MKTAVG is a synthetic cap-weighted index — dollar labels are misleading,
  // so show % anchored to the canonical Overview value (last bar maps EXACTLY
  // to the Overview gain/loss), falling back to the prev-window close.
  const isIndexPct = ticker === "MKTAVG";
  const pctBaseVal = useMemo(() => {
    if (!isIndexPct) return null;
    const priced = (stockBars || []).filter(d => (d.price || 0) > 0);
    const last = priced.length ? priced[priced.length - 1].price : null;
    if (last > 0 && canonVals?.market != null) return last / (1 + canonVals.market / 100);
    if (prevCloses.stock > 0) return prevCloses.stock;
    const first = priced[0];
    return first ? (first.open || first.price) : 100;
  }, [isIndexPct, stockBars, prevCloses.stock, canonVals]);
  const toPct = (v) => (v / (pctBaseVal || 100) - 1) * 100;
  const fmtVal = (v) => v == null ? "—"
    : isIndexPct ? `${toPct(v) >= 0 ? "+" : ""}${toPct(v).toFixed(2)}%`
    : `$${Number(v).toFixed(2)}`;

  const fmtVolume = (v) => {
    if (v == null) return "—";
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M`;
    if (v >= 1_000)     return `${(v / 1_000).toFixed(1)}K`;
    return `${v}`;
  };
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    // Pull raw volume + ATR from the active bar via payload[0].payload (all entries share it)
    const rawVol = payload[0]?.payload?.volume_raw;
    const rawAtr = payload[0]?.payload?.atr_raw;
    // OHLC for candle mode — surfaced at the top of the tooltip so users
    // can read exact open/high/low/close for the hovered bar.
    const o = payload[0]?.payload?.candle_open;
    const h = payload[0]?.payload?.candle_high;
    const l = payload[0]?.payload?.candle_low;
    const c = payload[0]?.payload?.candle_close;
    const showOhlc = chartType === "candle" && o != null && h != null && l != null && c != null;
    const up = c >= o;
    // Detect the candle pattern formed at THIS hovered bar (using bars
    // up to and including it — reversal patterns like Morning Star
    // need the 2 preceding bars for context).  Only in candle mode.
    let hoverPattern = null;
    if (showOhlc) {
      const idx = chartData.findIndex(d => d.time === label);
      if (idx >= 0) {
        hoverPattern = detectCandlePattern(chartData.slice(0, idx + 1));
      }
    }
    const pattColor = hoverPattern?.bias === "bullish"
      ? "text-emerald-300"
      : hoverPattern?.bias === "bearish" ? "text-rose-300" : "text-amber-300";
    return (
      <div className="bg-slate-900 border border-slate-700 rounded-lg p-3 shadow-xl text-xs">
        <p className="text-slate-300 mb-2 font-mono">{label}</p>
        {showOhlc && (
          <div className="mb-2 pb-2 border-b border-slate-700 font-mono">
            <div className={`text-[10px] font-bold uppercase tracking-wider mb-1 ${up ? "text-emerald-400" : "text-rose-400"}`}>
              {up ? "▲ UP" : "▼ DOWN"} · OHLC
            </div>
            <div className="grid grid-cols-4 gap-2 text-[11px]">
              <div><span className="text-slate-400">O:</span> <span className="text-white">{fmtVal(o)}</span></div>
              <div><span className="text-slate-400">H:</span> <span className="text-emerald-300">{fmtVal(h)}</span></div>
              <div><span className="text-slate-400">L:</span> <span className="text-rose-300">{fmtVal(l)}</span></div>
              <div><span className="text-slate-400">C:</span> <span className={up ? "text-emerald-300" : "text-rose-300"}>{fmtVal(c)}</span></div>
            </div>
            {/* Hover pattern — detected for THIS specific bar using the
                surrounding history.  Users can scan the chart bar-by-bar
                and read the pattern name + bias on hover. */}
            {hoverPattern ? (
              <div className="mt-2 pt-1 border-t border-slate-800">
                <div className={`text-[11px] font-bold ${pattColor}`}>
                  {hoverPattern.bias === "bullish" ? "▲" : hoverPattern.bias === "bearish" ? "▼" : "◆"} {hoverPattern.name}
                  <span className="ml-1.5 text-[9px] uppercase tracking-wider opacity-70">
                    · {hoverPattern.bias} · {hoverPattern.strength}
                  </span>
                </div>
                <p className="text-[10px] leading-tight text-slate-300 mt-0.5 whitespace-normal max-w-[280px] font-sans">
                  {hoverPattern.description}
                </p>
              </div>
            ) : (
              <div className="mt-1 text-[9px] text-slate-500 uppercase tracking-wider">
                no distinct pattern
              </div>
            )}
          </div>
        )}
        <div className="space-y-1">
          {payload.map((entry, i) => {
            // The candlestick Bar uses dataKey="candle_high" as an
            // anchor — hide it from the tooltip since it's an
            // internal implementation detail (users see the real
            // OHLC via the candle body/wick visually).
            if (entry.dataKey === "candle_high") return null;
            const isPctAxis = ["RSI", "MFI", "VIX"].includes(entry.name);
            const isVolume  = entry.name === "Volume";
            const isAtr     = entry.name === "ATR(14)";
            const isBookPct = entry.name?.startsWith("Book/Price %");
            let val;
            if (isVolume)      val = fmtVolume(rawVol);
            else if (isAtr)    val = rawAtr != null ? (isIndexPct ? `${rawAtr.toFixed(2)} pts` : `$${rawAtr.toFixed(2)}`) : "—";
            else if (isPctAxis || isBookPct) val = `${entry.value?.toFixed(2)}%`;
            else val = fmtVal(entry.value);
            return (
              <div key={i} className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: entry.color }} />
                <span className="text-slate-200">{entry.name}:</span>
                <span className="font-bold font-mono" style={{ color: entry.color }}>
                  {val}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Extend the price Y-axis domain so ATR reference lines / support /
  // resistance / EMAs / SMAs / benchmarks never get clipped below or above
  // the rendered Line data. Includes every series key whose toggle is
  // currently ON, and adds generous padding so toggled lines have room to
  // breathe instead of hugging the top/bottom edge of the chart.
  const priceYDomain = useMemo(() => {
    // Candle mode — fit the Y-axis TIGHTLY around the actual stock
    // OHLC range so each candle occupies enough vertical pixels for
    // its body + wicks to be individually distinguishable (matches
    // the standard TradingView / Yahoo candle chart look).  S/R
    // zones and other reference lines are still drawn, just often
    // outside the visible area — the user can flip to Line mode to
    // see the full extent.
    if (chartType === "candle") {
      const ohlc = [];
      // Match the render slice: we only render the last 60 bars in
      // candle mode, so tighten the Y-axis to just those bars.  This
      // is what gives the chart the tight Yahoo-style Y range (e.g.
      // $722-$724) instead of the full-day $10 spread.
      const visibleBars = chartData.slice(-60);
      visibleBars.forEach(d => {
        if (typeof d.candle_high === "number") ohlc.push(d.candle_high);
        if (typeof d.candle_low  === "number") ohlc.push(d.candle_low);
      });
      // Include visible S/R zones and ATR alert/stop levels so those
      // overlays don't fall off-screen when the user has them toggled
      // on.  Ignore MAs / benchmarks / regression / VWAP — including
      // them would stretch the axis and shrink the candles again.
      if (visible.zones && support != null)    ohlc.push(support);
      if (visible.zones && resistance != null) ohlc.push(resistance);
      if (visible.atr_alert && atrAlertLevel != null) ohlc.push(atrAlertLevel);
      if (visible.atr_stop  && atrStopLevel  != null) ohlc.push(atrStopLevel);
      if (ohlc.length) {
        const lo = Math.min(...ohlc);
        const hi = Math.max(...ohlc);
        const range = Math.max(hi - lo, 0.01);
        const pad = Math.max(range * 0.05, 0.10);  // tight 5% pad
        return [Math.floor((lo - pad) * 100) / 100, Math.ceil((hi + pad) * 100) / 100];
      }
    }
    const candidates = [];
    const benchKeys = ["stock","spy","dia","qqq","iwm","sector","subsector","regression","vwap"];
    const maKeys    = MA_CONFIG.map(m => m.key);
    chartData.forEach(d => {
      benchKeys.forEach(k => {
        if (visible[k] && typeof d[k] === "number" && Number.isFinite(d[k])) candidates.push(d[k]);
      });
      maKeys.forEach(k => {
        if (visible[k] && typeof d[k] === "number" && Number.isFinite(d[k])) candidates.push(d[k]);
      });
      // ETF fair-value line lives on the price axis
      if (visible.fair_value && typeof d.fair_value_price === "number" && Number.isFinite(d.fair_value_price)) {
        candidates.push(d.fair_value_price);
      }
      // Calculated iNAV line also on price axis
      if (visible.calc_inav && typeof d.calc_inav_price === "number" && Number.isFinite(d.calc_inav_price)) {
        candidates.push(d.calc_inav_price);
      }
    });
    // Anchor NAV — horizontal line; must fit in the price domain when visible
    if (visible.anchor_nav && fairValueSeries?.type === "ETF" && fairValueSeries?.anchor_value != null) {
      candidates.push(fairValueSeries.anchor_value);
    }
    if (visible.zones && support != null)    candidates.push(support);
    if (visible.zones && resistance != null) candidates.push(resistance);
    if (visible.atr_alert && atrAlertLevel != null) candidates.push(atrAlertLevel);
    if (visible.atr_stop  && atrStopLevel  != null) candidates.push(atrStopLevel);
    if (candidates.length === 0) return ["auto", "auto"];
    const lo = Math.min(...candidates);
    const hi = Math.max(...candidates);
    // Use 8% padding (was 4%) so toggled lines have visible breathing room
    // and the chart "expands" to include them rather than clipping at edges.
    const range = Math.max(hi - lo, 0.01);
    const pad = Math.max(range * 0.08, 0.75);
    return [Math.floor((lo - pad) * 100) / 100, Math.ceil((hi + pad) * 100) / 100];
  }, [chartData, visible, support, resistance, atrAlertLevel, atrStopLevel, chartType, fairValueSeries]);

  if (!ticker) return null;

  return (
    <Card className="bg-slate-900/40 border-slate-700" data-testid="multi-benchmark-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 text-white flex-wrap">
          <BarChart3 className="w-4 h-4 text-emerald-400" />
          Multi-Benchmark Comparison
          <span className="font-mono text-cyan-300 font-bold ml-1">{ticker}</span>
          <span className="text-[10px] text-slate-200 font-normal">
            · {timeframe.toUpperCase()} · rebased to stock starting price
          </span>
          <button onClick={load} disabled={loading}
                  data-testid="mbc-refresh"
                  className="ml-auto text-[11px] px-2 py-1 rounded border border-slate-500 hover:bg-slate-800 text-slate-100 flex items-center gap-1 disabled:opacity-50">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          {/* Line ⇄ Candle toggle.  Renders in the header next to Refresh so
              the control is discoverable and always in the same spot as the
              user changes timeframe.  The label under it shows the exact
              candle interval that will be drawn (5-minute / 1-hour / daily
              / monthly) so users know what each candle represents. */}
          <div className="inline-flex rounded border border-slate-500 overflow-hidden ml-1" data-testid="mbc-chart-type-group">
            <button
              type="button"
              onClick={() => setChartTypeSafely("line")}
              className={`text-[11px] px-2 py-1 flex items-center gap-1 ${chartType === "line" ? "bg-emerald-700 text-white" : "text-slate-300 hover:bg-slate-800"}`}
              data-testid="mbc-chart-type-line"
              title="Line chart"
            >
              <LineChartIcon className="w-3 h-3" /> Line
            </button>
            <button
              type="button"
              onClick={() => setChartTypeSafely("candle")}
              className={`text-[11px] px-2 py-1 flex items-center gap-1 border-l border-slate-500 ${chartType === "candle" ? "bg-emerald-700 text-white" : "text-slate-300 hover:bg-slate-800"}`}
              data-testid="mbc-chart-type-candle"
              title={`Candlestick chart · ${candleIntervalLabel} candles at ${timeframe.toUpperCase()}`}
            >
              <CandlestickChart className="w-3 h-3" /> Candle
            </button>
          </div>
          {/* AI Consolidation On/Off — analyzes candles for consolidation
              and draws dashed lines around each detected zone. */}
          {chartType === "candle" && (
            <div className="inline-flex items-center rounded border border-orange-500/70 overflow-hidden ml-1" data-testid="mbc-consolidation-group">
              <span className="px-2 py-1 text-[10px] font-bold text-orange-300 bg-orange-900/30 whitespace-nowrap">
                AI Consolidation{consolBusy ? "…" : showConsolidation && consolZones.length ? ` (${consolZones.length})` : ""}
              </span>
              <button
                type="button"
                onClick={() => setShowConsolidation(true)}
                data-testid="mbc-consolidation-on"
                className={`px-2 py-1 text-[10px] font-semibold transition-colors ${showConsolidation ? "bg-orange-600 text-white" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}
              >
                On
              </button>
              <button
                type="button"
                onClick={() => setShowConsolidation(false)}
                data-testid="mbc-consolidation-off"
                className={`px-2 py-1 text-[10px] font-semibold transition-colors ${!showConsolidation ? "bg-slate-600 text-white" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}
              >
                Off
              </button>
            </div>
          )}
          {/* Candle-interval dropdown — only relevant in candle mode.
              "Auto" = defer to the timeframe's default resolution
              (matches the label to the right).  Choosing an explicit
              interval triggers a re-fetch of ALL series (stock +
              benchmarks + fair-value) so bars stay aligned on the same
              X-axis. */}
          {chartType === "candle" && (
            <select
              value={candleInterval}
              onChange={(e) => setCandleInterval(e.target.value)}
              className="ml-1 text-[11px] bg-slate-900 border border-slate-500 rounded px-1.5 py-1 text-slate-100"
              data-testid="mbc-candle-interval"
              title="Candle bar interval"
            >
              <option value="auto">Auto ({defaultCandleLabel})</option>
              <option value="1m">1 minute</option>
              <option value="5m">5 minute</option>
              <option value="15m">15 minute</option>
              <option value="30m">30 minute</option>
              <option value="60m">1 hour</option>
              <option value="1d">1 day</option>
              <option value="1wk">1 week</option>
              <option value="1mo">1 month</option>
            </select>
          )}
          {chartType === "candle" && (
            <span className="text-[10px] text-emerald-300 font-normal ml-1 whitespace-nowrap">
              · {candleIntervalLabel} candles
            </span>
          )}
          {/* Excel-style horizontal zoom.  Uses a scaling wrapper div
              (min-width in %) instead of CSS `transform: scale()` so
              text/labels stay crisp at every zoom level.  100–500%. */}
          <div className="ml-2 flex items-center gap-1 border border-slate-500 rounded px-2 py-0.5" data-testid="mbc-zoom-group">
            <button
              type="button"
              onClick={() => setZoom(z => Math.max(100, z - 25))}
              disabled={zoom <= 100}
              className="text-slate-200 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="mbc-zoom-out"
              title="Zoom out"
            >−</button>
            <input
              type="range"
              min="100" max="500" step="25"
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="w-24 accent-emerald-500"
              data-testid="mbc-zoom-slider"
              title={`Zoom: ${zoom}%`}
            />
            <button
              type="button"
              onClick={() => setZoom(z => Math.min(500, z + 25))}
              disabled={zoom >= 500}
              className="text-slate-200 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="mbc-zoom-in"
              title="Zoom in"
            >+</button>
            <span
              className="text-[10px] font-mono text-slate-300 tabular-nums w-9 text-right cursor-pointer hover:text-white"
              onClick={() => setZoom(100)}
              title="Click to reset to 100%"
              data-testid="mbc-zoom-value"
            >
              {zoom}%
            </span>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {error && (
          <div className="text-rose-200 text-sm bg-rose-900/30 border border-rose-600 rounded px-3 py-2">
            {error}
          </div>
        )}

        {/* Toggle pills — split into two rows for benchmarks vs moving averages */}
        <div className="space-y-1.5" data-testid="mbc-toggles">
          <div className="flex flex-wrap gap-1.5">
            <span className="text-[10px] uppercase tracking-wider text-slate-200 font-bold self-center mr-1">
              Benchmarks
            </span>
            {lines.filter(l => l.kind !== "ma").map(l => {
              const on = visible[l.key];
              return (
                <button
                  key={l.key}
                  onClick={() => toggle(l.key)}
                  data-testid={`mbc-toggle-${l.key}`}
                  className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors
                    ${on
                      ? "bg-slate-800 border-slate-500 text-white"
                      : "bg-slate-900 border-slate-700 text-slate-400 hover:text-slate-100"}`}
                  style={on ? { boxShadow: `inset 0 0 0 2px ${l.color}` } : {}}
                >
                  {on ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: l.color }} />
                  {l.label}
                </button>
              );
            })}
            {/* Fair-value overlay toggle — dynamic label based on ticker type
                (ETF → "Fair Value (iNAV)" line on price axis;
                 Stock → "Book Value %" line on 0-100 right axis) */}
            {fairValueSeries?.series?.length > 0 && (
              <button
                onClick={() => toggle("fair_value")}
                data-testid="mbc-toggle-fair-value"
                title={fairValueSeries.type === "ETF"
                  ? `Weighted iNAV from ${fairValueSeries.n_holdings_used} underlying holdings (${(fairValueSeries.coverage_pct ?? 0).toFixed(1)}% coverage)`
                  : `Book value as % of price. Latest BVPS: $${(fairValueSeries.current_bvps ?? 0).toFixed(2)} · Now: ${(fairValueSeries.current_pct ?? 0).toFixed(2)}%`}
                className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors
                  ${visible.fair_value
                    ? "bg-slate-800 border-slate-500 text-white"
                    : "bg-slate-900 border-slate-700 text-slate-400 hover:text-slate-100"}`}
                style={visible.fair_value ? { boxShadow: "inset 0 0 0 2px #22d3ee" } : {}}
              >
                {visible.fair_value ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: "#22d3ee" }} />
                {fairValueSeries.type === "ETF" ? "Fair Value (iNAV)" : "Book Value %"}
              </button>
            )}
            {/* Calculated iNAV toggle — weight-based fallback (3rd method).
                Ratio approach: no SEC data / share counts needed, so it
                works for ETFs like SMH/XLK/ARKK where Primary breaks. */}
            {fairValueSeries?.type === "ETF" && fairValueSeries?.calc_series?.length > 0 && (
              <button
                onClick={() => toggle("calc_inav")}
                data-testid="mbc-toggle-calc-inav"
                title={
                  `Calc iNAV (3rd method) = anchor × Σ(wⱼ × pⱼ_now) / Σ(wⱼ × pⱼ_prev).\n`
                  + `Weight-based basket ratio. Fallback for ETFs where the primary iNAV fails.\n`
                  + `Anchor: $${(fairValueSeries.calc_meta?.anchor_value ?? 0).toFixed(2)}`
                }
                className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors
                  ${visible.calc_inav
                    ? "bg-slate-800 border-slate-500 text-white"
                    : "bg-slate-900 border-slate-700 text-slate-400 hover:text-slate-100"}`}
                style={visible.calc_inav ? { boxShadow: "inset 0 0 0 2px #fbbf24" } : {}}
              >
                {visible.calc_inav ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: "#fbbf24" }} />
                Calc iNAV (SEC)
              </button>
            )}
            {/* Anchor NAV toggle — only for ETFs; renders a horizontal
                reference line at yesterday's struck NAV so the user can
                see where the fund closed relative to today's live basket. */}
            {fairValueSeries?.type === "ETF" && fairValueSeries?.anchor_value != null && (
              <button
                onClick={() => toggle("anchor_nav")}
                data-testid="mbc-toggle-anchor-nav"
                title={`Yesterday's struck NAV = $${Number(fairValueSeries.anchor_value).toFixed(2)}. This is the anchor used for the live iNAV computation.`}
                className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors
                  ${visible.anchor_nav
                    ? "bg-slate-800 border-slate-500 text-white"
                    : "bg-slate-900 border-slate-700 text-slate-400 hover:text-slate-100"}`}
                style={visible.anchor_nav ? { boxShadow: "inset 0 0 0 2px #a78bfa" } : {}}
              >
                {visible.anchor_nav ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: "#a78bfa" }} />
                Anchor NAV&nbsp;${Number(fairValueSeries.anchor_value).toFixed(2)}
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            <span className="text-[10px] uppercase tracking-wider text-slate-200 font-bold self-center mr-1">
              Moving avgs
            </span>
            {lines.filter(l => l.kind === "ma").map(l => {
              const on = visible[l.key];
              const insufficient = stockBarCount > 0 && stockBarCount < l.period;
              return (
                <button
                  key={l.key}
                  onClick={() => !insufficient && toggle(l.key)}
                  disabled={insufficient}
                  title={insufficient ? `Need ${l.period} bars, have ${stockBarCount}` : ""}
                  data-testid={`mbc-toggle-${l.key}`}
                  className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors
                    ${insufficient
                      ? "bg-slate-950 border-slate-800 text-slate-600 cursor-not-allowed"
                      : on
                      ? "bg-slate-800 border-slate-500 text-white"
                      : "bg-slate-900 border-slate-700 text-slate-400 hover:text-slate-100"}`}
                  style={on && !insufficient ? { boxShadow: `inset 0 0 0 2px ${l.color}` } : {}}
                >
                  {insufficient ? <EyeOff className="w-3 h-3" /> : on ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: l.color }} />
                  {l.label}
                </button>
              );
            })}
          </div>

          {/* Right-axis indicators (RSI / MFI / VIX) + support/resistance zones */}
          <div className="flex flex-wrap gap-1.5">
            <span className="text-[10px] uppercase tracking-wider text-slate-200 font-bold self-center mr-1">
              Indicators
            </span>
            {RIGHT_AXIS_INDICATORS.map(ind => {
              const on = visible[ind.key];
              return (
                <button
                  key={ind.key}
                  onClick={() => toggle(ind.key)}
                  data-testid={`mbc-toggle-${ind.key}`}
                  className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors
                    ${on
                      ? "bg-slate-800 border-slate-500 text-white"
                      : "bg-slate-900 border-slate-700 text-slate-400 hover:text-slate-100"}`}
                  style={on ? { boxShadow: `inset 0 0 0 2px ${ind.color}` } : {}}
                >
                  {on ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: ind.color }} />
                  {ind.label}
                </button>
              );
            })}
            {/* Support/Resistance zones toggle */}
            <button
              onClick={() => toggle("zones")}
              data-testid="mbc-toggle-zones"
              className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors
                ${visible.zones
                  ? "bg-slate-800 border-slate-500 text-white"
                  : "bg-slate-900 border-slate-700 text-slate-400 hover:text-slate-100"}`}
              style={visible.zones ? { boxShadow: "inset 0 0 0 2px #10b981" } : {}}
            >
              {visible.zones ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
              <span className="w-2 h-2 rounded-full" style={{ background: "linear-gradient(to bottom, #ef4444 50%, #10b981 50%)" }} />
              Support / Resistance
            </button>

            {/* 1× ATR trailing-alert reference line */}
            <button
              onClick={() => toggle("atr_alert")}
              disabled={atrAlertLevel == null}
              title={atrAlertLevel == null ? "Need ≥3 bars to compute ATR" : `1× ATR alert @ ${fmtVal(atrAlertLevel)}`}
              data-testid="mbc-toggle-atr-alert"
              className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors
                ${atrAlertLevel == null
                  ? "bg-slate-950 border-slate-800 text-slate-600 cursor-not-allowed"
                  : visible.atr_alert
                  ? "bg-slate-800 border-slate-500 text-white"
                  : "bg-slate-900 border-slate-700 text-slate-400 hover:text-slate-100"}`}
              style={visible.atr_alert && atrAlertLevel != null ? { boxShadow: "inset 0 0 0 2px #facc15" } : {}}
            >
              {visible.atr_alert && atrAlertLevel != null ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: "#facc15" }} />
              1× ATR alert
              {atrAlertLevel != null && (
                <span className="font-mono text-[10px] text-slate-200 ml-0.5">{fmtVal(atrAlertLevel)}</span>
              )}
            </button>

            {/* 2× ATR stop reference line (only meaningful if ATR was computed) */}
            <button
              onClick={() => toggle("atr_stop")}
              disabled={atrStopLevel == null}
              title={atrStopLevel == null ? "Need ≥3 bars to compute ATR" : `2× ATR stop @ ${fmtVal(atrStopLevel)}`}
              data-testid="mbc-toggle-atr-stop"
              className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors
                ${atrStopLevel == null
                  ? "bg-slate-950 border-slate-800 text-slate-600 cursor-not-allowed"
                  : visible.atr_stop
                  ? "bg-slate-800 border-slate-500 text-white"
                  : "bg-slate-900 border-slate-700 text-slate-400 hover:text-slate-100"}`}
              style={visible.atr_stop && atrStopLevel != null ? { boxShadow: "inset 0 0 0 2px #f43f5e" } : {}}
            >
              {visible.atr_stop && atrStopLevel != null ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: "#f43f5e" }} />
              2× ATR stop
              {atrStopLevel != null && (
                <span className="font-mono text-[10px] text-slate-200 ml-0.5">{fmtVal(atrStopLevel)}</span>
              )}
            </button>

            {/* Shaded warning zone band between 1× and 2× ATR */}
            <button
              onClick={() => toggle("atr_zone")}
              disabled={atrAlertLevel == null || atrStopLevel == null}
              title={atrAlertLevel == null || atrStopLevel == null
                ? "Need ≥3 bars to compute ATR"
                : `Warning zone ${fmtVal(atrStopLevel)} – ${fmtVal(atrAlertLevel)}`}
              data-testid="mbc-toggle-atr-zone"
              className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-semibold border transition-colors
                ${atrAlertLevel == null || atrStopLevel == null
                  ? "bg-slate-950 border-slate-800 text-slate-600 cursor-not-allowed"
                  : visible.atr_zone
                  ? "bg-slate-800 border-slate-500 text-white"
                  : "bg-slate-900 border-slate-700 text-slate-400 hover:text-slate-100"}`}
              style={visible.atr_zone && atrAlertLevel != null && atrStopLevel != null
                ? { boxShadow: "inset 0 0 0 2px #f59e0b" } : {}}
            >
              {visible.atr_zone && atrAlertLevel != null && atrStopLevel != null
                ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: "#f59e0b" }} />
              Warning zone
            </button>
          </div>
        </div>

        {/* Chart — taller (520px) so toggled EMAs/SMAs/indicators are
            distinguishable and never visually crammed against the stock line. */}
        {loading && chartData.length === 0 ? (
          <Skeleton className="h-[520px] bg-slate-800/60" data-testid="mbc-skel" />
        ) : chartData.length === 0 ? (
          <div className="h-[520px] flex items-center justify-center text-slate-300 text-sm">
            No comparison data available.
          </div>
        ) : chartType === "candle" ? (
          // Yahoo-Finance-identical candlestick chart, powered by
          // TradingView's Lightweight Charts library (the same rendering
          // engine used by Yahoo itself).  Overlays (MAs, benchmarks,
          // regression, VWAP, iNAV, Anchor NAV, S/R zones, ATR bands)
          // are wired via `visible` + `overlays` + `levels` props so the
          // toggle chips paint the candle chart exactly like Line mode.
          <YahooStyleCandlestick
            percentBase={isIndexPct ? pctBaseVal : null}
            ticker={ticker}
            timeframe={timeframe}
            candleInterval={candleInterval}
            height={620}
            bars={chartData}
            consolidationZones={showConsolidation ? consolZones : null}
            visible={visible}
            overlays={lines.filter(l => l.key !== "stock")
              .map(l => ({
                key: l.key,
                label: l.label,
                color: l.color,
                strokeWidth: l.strokeWidth,
                strokeDasharray: l.strokeDasharray,
              }))
              .concat(
                fairValueSeries?.type === "ETF" && fairValueSeries?.series?.length > 0
                  ? [{ key: "fair_value_price", label: "iNAV",
                       color: "#22d3ee", strokeWidth: 2.2, strokeDasharray: "6 4" }]
                  : []
              )
              .concat(
                fairValueSeries?.type === "ETF" && fairValueSeries?.calc_series?.length > 0
                  ? [{ key: "calc_inav_price", label: "Calc iNAV (SEC)",
                       color: "#fbbf24", strokeWidth: 2, strokeDasharray: "3 3" }]
                  : []
              )}
            levels={{
              anchorNav: fairValueSeries?.type === "ETF" ? fairValueSeries?.anchor_value : null,
              support, resistance,
              atrAlert: atrAlertLevel, atrStop: atrStopLevel, lastAtr,
            }}
            showZones={visible.zones}
            showAtrAlert={visible.atr_alert}
            showAtrStop={visible.atr_stop}
            showAtrZone={visible.atr_zone}
            showAnchorNav={visible.anchor_nav}
          />
        ) : (
          // Zoom wrapper — horizontally scrollable when the inner chart
          // is wider than 100%.  Using `minWidth` (not `transform:scale`)
          // keeps text/labels crisp at every zoom level and lets the
          // browser handle scrolling natively.  ResponsiveContainer
          // then fills that wider div, so bars/candles spread out
          // proportionally as `zoom` climbs from 100% → 500%.
          <div
            className="overflow-x-auto overflow-y-hidden"
            data-testid="mbc-chart-scroll"
          >
            <div style={{ minWidth: `max(${zoom}%, 640px)`, height: chartType === "candle" ? 720 : 520 }}>
              <ResponsiveContainer width="100%" height={chartType === "candle" ? 720 : 520}>
                {/* In candle mode we render only the last 60 bars by
                    default — this matches how Yahoo Finance /
                    TradingView open their candle view (the user scrolled
                    into a narrow ~1-hour window in their reference).
                    Fewer bars = wider bar band = chunky Yahoo-style
                    candles instead of tick-dust.  The zoom slider still
                    stretches the chart horizontally on top of this, so
                    when the user zooms out to 500% they get an even
                    wider render of the same 60 bars.  This slice ALSO
                    tightens the Y-axis (see priceYDomain above — it
                    already keys off the chartData high/low, so slicing
                    trims stale early-day price extremes and gives the
                    tight Yahoo-like $2-range look. */}
                <ComposedChart data={chartType === "candle" ? chartData.slice(-60) : chartData} margin={{ top: 10, right: 50, left: 10, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="time" stroke="#cbd5e1" tick={{ fill: '#cbd5e1', fontSize: 9 }} />
              <YAxis yAxisId="price" domain={priceYDomain} stroke="#cbd5e1"
                     tick={{ fill: '#cbd5e1', fontSize: 9 }}
                     tickFormatter={(v) => isIndexPct ? `${toPct(v) >= 0 ? "+" : ""}${toPct(v).toFixed(1)}%` : `$${v.toFixed(0)}`} />
              {/* Right-axis 0-100 for RSI / MFI / VIX. */}
              <YAxis yAxisId="ind" orientation="right" domain={[0, 100]} stroke="#94a3b8"
                     tick={{ fill: '#94a3b8', fontSize: 9 }}
                     tickFormatter={(v) => `${v}`} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ paddingTop: '6px', fontSize: '10px' }} iconType="circle" />

              {/* Support / resistance zone (price axis) */}
              {visible.zones && support != null && resistance != null && (
                <>
                  <ReferenceArea yAxisId="price" y1={support} y2={support * 1.01}
                                 fill="#10b981" fillOpacity={0.18} stroke="#10b981" strokeOpacity={0.5} strokeDasharray="3 3" />
                  <ReferenceLine yAxisId="price" y={support} stroke="#10b981" strokeDasharray="4 2"
                                 label={{ value: `Support ${isIndexPct ? fmtVal(support) : support.toFixed(2)}`, position: "insideBottomLeft",
                                          fill: "#34d399", fontSize: 10, fontWeight: 600 }} />
                  <ReferenceArea yAxisId="price" y1={resistance * 0.99} y2={resistance}
                                 fill="#ef4444" fillOpacity={0.18} stroke="#ef4444" strokeOpacity={0.5} strokeDasharray="3 3" />
                  <ReferenceLine yAxisId="price" y={resistance} stroke="#ef4444" strokeDasharray="4 2"
                                 label={{ value: `Resistance ${isIndexPct ? fmtVal(resistance) : resistance.toFixed(2)}`, position: "insideTopLeft",
                                          fill: "#f87171", fontSize: 10, fontWeight: 600 }} />
                </>
              )}

              {/* 1× ATR trailing-alert reference line (warns before the full stop hits) */}
              {visible.atr_alert && atrAlertLevel != null && (
                <ReferenceLine yAxisId="price" y={atrAlertLevel}
                               stroke="#facc15" strokeWidth={2} strokeDasharray="6 3"
                               label={{ value: `1× ATR alert ${isIndexPct ? fmtVal(atrAlertLevel) : atrAlertLevel.toFixed(2)}`,
                                        position: "insideTopRight",
                                        fill: "#fde047", fontSize: 10, fontWeight: 600 }} />
              )}

              {/* 2× ATR stop reference line (suggested swing-stop) */}
              {visible.atr_stop && atrStopLevel != null && (
                <ReferenceLine yAxisId="price" y={atrStopLevel}
                               stroke="#f43f5e" strokeWidth={2} strokeDasharray="6 3"
                               label={{ value: `2× ATR stop ${isIndexPct ? fmtVal(atrStopLevel) : atrStopLevel.toFixed(2)} (ATR ${lastAtr?.toFixed(2)})`,
                                        position: "insideTopRight",
                                        fill: "#fda4af", fontSize: 10, fontWeight: 600 }} />
              )}

              {/* Shaded warning zone between 1× ATR alert and 2× ATR stop */}
              {visible.atr_zone && atrAlertLevel != null && atrStopLevel != null && (
                <ReferenceArea yAxisId="price" y1={atrStopLevel} y2={atrAlertLevel}
                               fill="#f59e0b" fillOpacity={0.18}
                               stroke="#f59e0b" strokeOpacity={0.35} strokeDasharray="3 3" />
              )}

              {/* Fair-value overlay:
                    • ETFs → live iNAV time-series on the PRICE axis
                             (moves with the underlying basket, so any
                             gap between it and the ETF price = premium/
                             discount that changes over time).
                    • Stocks → book-value-as-% of price on the RIGHT 0-100
                             axis (rises when the stock cheapens vs. book,
                             falls when it richens). */}
              {visible.fair_value && fairValueSeries?.type === "ETF" && (
                <Line yAxisId="price" type="monotone" dataKey="fair_value_price"
                  name={`iNAV · ${fairValueSeries.n_holdings_used ?? ""}h`}
                  stroke="#22d3ee" strokeWidth={2.2} strokeDasharray="6 4"
                  dot={false} connectNulls isAnimationActive={false} />
              )}
              {/* Calculated iNAV (3rd method) — amber, dashed differently. */}
              {visible.calc_inav && fairValueSeries?.type === "ETF" && (
                <Line yAxisId="price" type="monotone" dataKey="calc_inav_price"
                  name={`Calc iNAV (SEC)`}
                  stroke="#fbbf24" strokeWidth={2} strokeDasharray="3 3"
                  dot={false} connectNulls isAnimationActive={false} />
              )}
              {visible.fair_value && fairValueSeries?.type === "STOCK" && (
                <Line yAxisId="ind" type="monotone" dataKey="fair_value_pct"
                  name={`Book/Price % · $${(fairValueSeries.current_bvps ?? 0).toFixed(2)} BVPS`}
                  stroke="#22d3ee" strokeWidth={2.2} strokeDasharray="6 4"
                  dot={false} connectNulls isAnimationActive={false} />
              )}
              {/* Anchor NAV — horizontal reference line at yesterday's
                  struck NAV (only meaningful for ETFs).  Purple to
                  distinguish from the moving iNAV curve. */}
              {visible.anchor_nav && fairValueSeries?.type === "ETF" && fairValueSeries?.anchor_value != null && (
                <ReferenceLine yAxisId="price" y={fairValueSeries.anchor_value}
                  stroke="#a78bfa" strokeWidth={1.8} strokeDasharray="2 4"
                  label={{
                    value: `Anchor NAV $${Number(fairValueSeries.anchor_value).toFixed(2)}`,
                    position: "insideTopRight",
                    fill: "#c4b5fd",
                    fontSize: 10,
                    fontWeight: 700,
                  }} />
              )}

              {/* Price-axis lines */}
              {/* Price-axis lines.  When chartType === "candle", we hide
                  the raw stock price line since the candlestick layer
                  below IS the price series — all other overlays (MAs,
                  VWAP, benchmarks, regression, fair value) stay on. */}
              {lines.map(l => (
                visible[l.key] && !(chartType === "candle" && l.key === "stock") && (
                <Line key={l.key}
                  yAxisId="price"
                  type="monotone"
                  dataKey={l.key}
                  name={l.label}
                  stroke={l.color}
                  strokeWidth={l.strokeWidth}
                  strokeDasharray={l.strokeDasharray}
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                />
              )))}

              {/* Right-axis indicator lines (RSI / MFI / VIX) */}
              {RIGHT_AXIS_INDICATORS.map(ind => visible[ind.key] && (
                <Line key={ind.key}
                  yAxisId="ind"
                  type="monotone"
                  dataKey={ind.key}
                  name={ind.label}
                  stroke={ind.color}
                  strokeWidth={1.8}
                  strokeDasharray={ind.strokeDasharray}
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                />
              ))}

              {/* Candlestick series — a Bar per data point, keyed on
                  `candle_high` so its pixel rect is anchored to the
                  price axis (matches every other price-axis element).
                  Its default rectangle is REPLACED by <CandleShape/>
                  which draws wick + body from OHLC in the payload.
                  The stock's normal price Line is auto-hidden when
                  chartType === "candle" (see the `lines` map render
                  block above) so the candles are the primary price
                  series. */}
              {chartType === "candle" && (
                <Bar
                  yAxisId="price"
                  dataKey="candle_high"
                  shape={<CandleShape />}
                  isAnimationActive={false}
                  legendType="none"
                  data-testid="mbc-candles"
                />
              )}
            </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Performance box */}
        {performance && (
          <div data-testid="mbc-perf-box" className="border border-slate-700 rounded-lg overflow-hidden">
            <div className="grid grid-cols-2 lg:grid-cols-7 text-xs">
              {/* Stock cell */}
              <div className="p-2 bg-slate-900/60 border-r border-slate-800" data-testid="mbc-perf-stock">
                <div className="text-[10px] uppercase tracking-wider text-purple-300 font-bold">
                  {ticker} · {timeframe.toUpperCase()}
                </div>
                <div className={`font-mono text-base font-bold ${pctClass(performance.stockPct)}`}>
                  {fmtPct(performance.stockPct)}
                </div>
                <div className="text-[10px] font-mono text-slate-300">
                  ${performance.stockStart?.toFixed(2)} → ${performance.stockEnd?.toFixed(2)}
                </div>
              </div>

              {/* Benchmark cells (only show the ones with data) */}
              {INDEX_BENCHMARKS.map(b => (
                <BenchPerfCell key={b.key} testid={`mbc-perf-${b.key}`} ticker={ticker}
                               label={b.label} symbol={b.symbol} color={b.color}
                               perf={performance[b.key]} timeframe={timeframe} />
              ))}
              {etfMap.sector_etf && (
                <BenchPerfCell testid="mbc-perf-sector" ticker={ticker}
                               label={etfMap.sector_label || "Sector"}
                               symbol={etfMap.sector_etf} color="#34d399"
                               perf={performance.sector} timeframe={timeframe} />
              )}
              {etfMap.subsector_etf && (
                <BenchPerfCell testid="mbc-perf-subsector" ticker={ticker}
                               label={etfMap.subsector_label || "Subsector"}
                               symbol={etfMap.subsector_etf} color="#a3e635"
                               perf={performance.subsector} timeframe={timeframe} />
              )}
            </div>
          </div>
        )}

        {regressionStats && (
          <div className="text-[11px] text-slate-200 font-mono">
            Regression direction: <span className={`font-bold ${regressionStats.slope >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
              {regressionStats.slope >= 0 ? "UP" : "DOWN"}
            </span>
            <span className="ml-2 text-slate-100">
              slope/bar {regressionStats.slope >= 0 ? '+' : ''}{regressionStats.slope?.toFixed(3)}
            </span>
            <span className="ml-2 text-slate-100">
              fit ${regressionStats.startPrice?.toFixed(2)} → ${regressionStats.endPrice?.toFixed(2)}
            </span>
          </div>
        )}

        {/* Candle-pattern info box — appears only in candle mode.
            Analyzes the last 3 bars of the current chart series and
            surfaces the recognised pattern name, bias (bullish /
            bearish / neutral), strength, and a short description
            drawn from the classic candlestick cheat-sheet. */}
        {chartType === "candle" && (() => {
          const patt = detectCandlePattern(chartData);
          const bar = chartData?.[chartData.length - 1];
          const biasColor = patt?.bias === "bullish" ? "text-emerald-300 border-emerald-600 bg-emerald-950/40"
            : patt?.bias === "bearish" ? "text-rose-300 border-rose-600 bg-rose-950/40"
            : "text-amber-300 border-amber-600 bg-amber-950/40";
          const biasIcon = patt?.bias === "bullish" ? "▲" : patt?.bias === "bearish" ? "▼" : "◆";
          return (
            <div
              className={`rounded border ${patt ? biasColor : "bg-slate-900 border-slate-600 text-slate-300"} px-3 py-2 text-xs mt-2`}
              data-testid="mbc-candle-pattern-box"
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-wider font-bold opacity-70">
                  Latest Candle
                </span>
                {bar && (
                  <span className="font-mono text-[11px] text-slate-100">{bar.time}</span>
                )}
                {patt ? (
                  <>
                    <span className="text-[11px] font-bold ml-1" data-testid="mbc-candle-pattern-name">
                      {biasIcon} {patt.name}
                    </span>
                    <span className="text-[10px] font-semibold uppercase tracking-wider">
                      · {patt.bias}
                    </span>
                    <span className="text-[10px] uppercase opacity-70">
                      · {patt.strength}
                    </span>
                  </>
                ) : (
                  <span className="text-[11px]">No distinct pattern on the most recent candle.</span>
                )}
              </div>
              {patt && (
                <p className="mt-1 text-[11px] leading-snug text-slate-200/90" data-testid="mbc-candle-pattern-desc">
                  {patt.description}
                </p>
              )}
            </div>
          );
        })()}
      </CardContent>
    </Card>
  );
}

function BenchPerfCell({ ticker, label, symbol, color, perf, timeframe, testid }) {
  if (!perf || perf.pct == null) {
    return (
      <div className="p-2 border-r border-slate-800 last:border-r-0" data-testid={testid}>
        <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color }}>
          {symbol} · {label}
        </div>
        <div className="text-[11px] text-slate-400 italic mt-1">loading…</div>
      </div>
    );
  }
  return (
    <div className="p-2 border-r border-slate-800 last:border-r-0" data-testid={testid}>
      <div className="flex items-center gap-1">
        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
        <a href={`/stock?ticker=${symbol}`} target="_blank" rel="noopener noreferrer"
           className="text-[10px] uppercase tracking-wider font-bold hover:underline" style={{ color }}>
          {symbol}
        </a>
        <span className="text-[10px] text-slate-200 truncate">· {label}</span>
      </div>
      <div className={`font-mono text-base font-bold ${pctClass(perf.pct)}`}>{fmtPct(perf.pct)}</div>
      <div className={`text-[10px] font-mono font-bold ${perf.diff >= 0 ? "text-emerald-200" : "text-rose-200"}`}>
        {ticker} − {symbol}: {fmtPct(perf.diff)}
      </div>
      <div className="text-[9px] font-mono text-slate-400">
        ${perf.start?.toFixed(2)} → ${perf.end?.toFixed(2)}
      </div>
    </div>
  );
}
